import { useEffect } from 'react'
import { useRouteError } from 'react-router-dom'

import { Button } from '@/components/ui/button.jsx'
import { TypographyP, TypographySmallMuted } from '@/components/ui/typography.jsx'
import { handleError } from '@/services/errorHandler.js'

// What is this?
// Since we use dynamic imports using the native 'lazy' function from React, there might be cases where the dynamic modules won't be available and the app will crush, why?
// Because if the user using the app on his browser, then we deploy new version to vercel, vercel wil automatically remove the old files meaning the dynamic modules of the previous version (the version the user currently uses) won't be available any more
// So we catch this error and reload the page to make sure the user gets the new version of the app
export const handleDynamicRouteError = (error, info) => {
    // Report the error
    handleError(error, { info, context: 'LoadableErrorBoundary' })

    // Check if it's the error we are looking for - '... failed to fetch dynamically imported module ...'
    if (error?.message?.toLowerCase()?.includes('failed to fetch dynamically imported module')) {

        const sessionStorageKey = 'reload-on-dynamic-import-error'
        const reloadOnDynamicImportErrorAlreadyReloaded = sessionStorage.getItem(sessionStorageKey)

        // Add a check this was already happened so we won't cause endless reload, we allow this 5 times per session
        if (reloadOnDynamicImportErrorAlreadyReloaded && Number(reloadOnDynamicImportErrorAlreadyReloaded) > 10) {
            return
        }

        // Set the storage count and reload the page
        sessionStorage.setItem('reload-on-dynamic-import-error', String(Number(reloadOnDynamicImportErrorAlreadyReloaded ? reloadOnDynamicImportErrorAlreadyReloaded : 0) + 1))
        window.location.reload()
    }
}

export const AppErrorBoundary = () => {
    return (
        <div className="m-auto text-center h-full w-full flex items-center justify-center rounded-md flex-col gap-1">
            <TypographyP>Unexpected error occurred</TypographyP>
            <TypographySmallMuted className="font-light">We are sorry for the inconvenience. The dev team has been notified</TypographySmallMuted>
            <Button
                size="sm"
                className="mt-2"
                onClick={() => window.location.reload()}
            >
                Refresh the page
            </Button>
        </div>
    )
}


// Router-level fallback (used as `errorElement`) for errors that escape every <ErrorBoundary>,
// otherwise React Router renders its default screen with the raw stack trace
export const RouteErrorBoundary = () => {

    const error = useRouteError()

    useEffect(() => {
        handleDynamicRouteError(error, { context: 'RouteErrorBoundary' })
    }, [error])

    return (
        <div className="h-screen w-full">
            <AppErrorBoundary />
        </div>
    )
}
